import { useEffect, useState, useCallback } from "react";
import { adminApi } from "@/lib/adminApi";
import { fmtDate, fmtMoney, statusBadge } from "@/pages/admin/util";
import { Loader2, RefreshCw, Users, BadgePoundSterling, CalendarClock, UserPlus, AlertCircle } from "lucide-react";
import MemberDetailDialog from "@/pages/admin/MemberDetailDialog";

const STATUSES = ["", "ACTIVE", "PENDING", "PAST_DUE", "CANCELLED"];

export default function Members() {
  const [items, setItems] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, dash] = await Promise.all([
        adminApi.get("/members", { params: statusFilter ? { status: statusFilter } : {} }),
        adminApi.get("/membership-dashboard"),
      ]);
      setItems(list.data.items);
      setDashboard(dash.data);
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const k = dashboard?.kpis;

  return (
    <div data-testid="admin-members-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="eyebrow">Recurring revenue</span>
          <h1 className="font-display text-[32px] lg:text-[40px] leading-[1.05] tracking-tight text-[#071A33] mt-2">Members</h1>
        </div>
        <div className="flex items-center gap-2">
          <select className="field-input h-10" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="members-status-filter">
            {STATUSES.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
          </select>
          <button onClick={load} className="btn-outline h-10 py-0 px-4" data-testid="members-refresh"><RefreshCw size={14} className={loading ? "animate-spin" : ""}/></button>
        </div>
      </div>

      {/* KPI strip — answers "how healthy is recurring revenue?" at a glance. */}
      {k && (
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="members-kpi-strip">
          <KpiTile testid="members-kpi-active" label="Active" value={k.active} icon={<Users size={14}/>} tone="green"/>
          <KpiTile testid="members-kpi-new-month" label="New this month" value={k.new_this_month} icon={<UserPlus size={14}/>} tone="teal"/>
          <KpiTile testid="members-kpi-expiring" label="Expiring in 30 days" value={k.expiring_30_days} icon={<CalendarClock size={14}/>} tone={k.expiring_30_days > 0 ? "amber" : "muted"}/>
          <KpiTile testid="members-kpi-past-due" label="Past due" value={k.past_due} icon={<AlertCircle size={14}/>} tone={k.past_due > 0 ? "red" : "muted"}/>
          <KpiTile testid="members-kpi-mrr" label="Est. MRR" value={fmtMoney(k.mrr_estimate)} icon={<BadgePoundSterling size={14}/>} tone="muted"/>
        </div>
      )}

      <div className="mt-6 admin-panel overflow-x-auto">
        <table className="w-full text-sm" data-testid="members-table">
          <thead>
            <tr className="bg-[#F7F4ED] text-left text-[#475569] text-xs uppercase tracking-[0.15em]">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Park</th>
              <th className="px-4 py-3">Plot</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Renewal</th>
              <th className="px-4 py-3">Cooling-off</th>
              <th className="px-4 py-3">Service used</th>
              <th className="px-4 py-3">Stripe customer</th>
              <th className="px-4 py-3">Joined</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={11} className="px-4 py-8 text-center text-[#475569]"><Loader2 className="animate-spin inline" size={16}/></td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={11} className="px-4 py-8 text-center text-[#475569]">No members yet.</td></tr>}
            {items.map((m) => (
              <tr key={m.id} className="border-t border-[#E2DCD0]">
                <td className="px-4 py-3 font-medium text-[#071A33]">{m.full_name}</td>
                <td className="px-4 py-3 text-[#475569]">{m.email}</td>
                <td className="px-4 py-3 text-[#475569]">{m.park_name || "—"}</td>
                <td className="px-4 py-3 text-[#475569]">{m.plot_number || "—"}</td>
                <td className="px-4 py-3">{statusBadge(m.membership_status)}</td>
                <td className="px-4 py-3 text-[#475569]">{fmtDate(m.current_period_end, true)}</td>
                <td className="px-4 py-3 text-[#475569]">{m.cooling_off_waived ? "Waived" : "Active"}</td>
                <td className="px-4 py-3 text-[#475569]">{fmtDate(m.service_used_at, true)}</td>
                <td className="px-4 py-3 text-[#475569] font-mono text-[11px]">{m.stripe_customer_id || "—"}</td>
                <td className="px-4 py-3 text-[#475569]">{fmtDate(m.created_at, true)}</td>
                <td className="px-4 py-3 text-right">
                  <button className="text-[#064E2E] text-xs font-medium hover:underline" onClick={() => setOpenId(m.id)} data-testid={`member-view-${m.id}`}>View →</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <MemberDetailDialog memberId={openId} onClose={() => setOpenId(null)} onUpdated={load}/>
    </div>
  );
}

function KpiTile({ label, value, icon, tone, testid }) {
  const cls = tone === "green" ? "border-[#064E2E]/30 bg-[#064E2E]/5"
            : tone === "teal"  ? "border-[#0F766E]/30 bg-[#0F766E]/5"
            : tone === "amber" ? "border-[#B88A2A]/30 bg-[#B88A2A]/5"
            : tone === "red"   ? "border-[#B91C1C]/30 bg-[#B91C1C]/5"
            : "border-[#E2DCD0] bg-white";
  return (
    <div className={`border rounded-xl p-4 ${cls}`} data-testid={testid}>
      <p className="text-[10px] tracking-[0.18em] uppercase text-[#475569] flex items-center gap-1.5">{icon}{label}</p>
      <p className="font-display text-3xl text-[#071A33] tabular-nums mt-1 tracking-tight">{value}</p>
    </div>
  );
}
